import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import FormField, { Input, Select } from "../components/ui/FormField";
import styles from "./Employees.module.css";

const EMPTY = { full_name: "", email: "", phone: "", role: "staff", department: "", hire_date: "", status: "active" };

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null); // null = new
  const [form,      setForm]      = useState(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState("");

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("employees")
      .select("*")
      .order("full_name");
    setEmployees(data ?? []);
    setLoading(false);
  }

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setErr("");
    setModal(true);
  }

  function openEdit(emp) {
    setEditing(emp);
    setForm({ ...EMPTY, ...emp });
    setErr("");
    setModal(true);
  }

  function set(field, val) { setForm((f) => ({ ...f, [field]: val })); }

  async function handleSave() {
    if (!form.full_name.trim() || !form.email.trim()) { setErr("Name and email are required."); return; }
    setSaving(true);
    setErr("");

    const payload = {
      full_name:  form.full_name.trim(),
      email:      form.email.trim(),
      phone:      form.phone.trim() || null,
      role:       form.role,
      department: form.department || null,
      hire_date:  form.hire_date  || null,
      status:     form.status,
    };

    let error;
    if (editing) {
      ({ error } = await supabase.from("employees").update(payload).eq("id", editing.id));
    } else {
      // Create auth user via admin invite — for now just insert the record
      // The user will receive an invite email set up in Supabase Auth settings
      const { data: authData, error: authErr } = await supabase.auth.admin?.inviteUserByEmail?.(form.email) ?? {};
      ({ error } = await supabase.from("employees").insert({
        ...payload,
        auth_id: authData?.user?.id ?? null,
      }));
      if (authErr) console.warn("Auth invite:", authErr.message);
    }

    setSaving(false);
    if (error) { setErr(error.message); return; }
    setModal(false);
    load();
  }

  async function toggleStatus(emp) {
    const next = emp.status === "active" ? "inactive" : "active";
    await supabase.from("employees").update({ status: next }).eq("id", emp.id);
    setEmployees((prev) => prev.map((e) => e.id === emp.id ? { ...e, status: next } : e));
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <span className={styles.count}>{employees.length} employee{employees.length !== 1 ? "s" : ""}</span>
        <button className={styles.addBtn} onClick={openNew}>+ Add Employee</button>
      </div>

      <div className={styles.tableWrap}>
        {loading ? (
          <div className={styles.empty}>Loading…</div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Hire Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.id} className={styles.row}>
                    <td><strong>{emp.full_name}</strong></td>
                    <td>{emp.email}</td>
                    <td>{emp.phone ?? "—"}</td>
                    <td style={{ textTransform: "capitalize" }}>{emp.role}</td>
                    <td>{emp.department ?? "—"}</td>
                    <td>{emp.hire_date ?? "—"}</td>
                    <td><Badge label={emp.status} /></td>
                    <td>
                      <div className={styles.actions}>
                        <button className={styles.editBtn} onClick={() => openEdit(emp)}>Edit</button>
                        <button className={styles.toggleBtn} onClick={() => toggleStatus(emp)}>
                          {emp.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {employees.length === 0 && (
                  <tr><td colSpan={8} className={styles.empty}>No employees yet.</td></tr>
                )}
              </tbody>
            </table>

            {/* Mobile Employee Cards */}
            <div className={styles.mobileList}>
              {employees.map((emp) => (
                <div key={emp.id} className={styles.empCard}>
                  <div className={styles.empHeader}>
                    <div className={styles.empAvatar}>
                      {emp.full_name?.[0]?.toUpperCase() ?? "?"}
                    </div>
                    <div className={styles.empInfo}>
                      <div className={styles.empName}>{emp.full_name}</div>
                      <div className={styles.empEmail}>{emp.email}</div>
                      {emp.phone && <div className={styles.empPhone}>📞 {emp.phone}</div>}
                    </div>
                    <Badge label={emp.status} />
                  </div>

                  <div className={styles.empDetails}>
                    <div className={styles.empDetailItem}>
                      <span className={styles.empDetailLabel}>Role:</span>
                      <span className={styles.empDetailVal} style={{ textTransform: "capitalize" }}>
                        {emp.role}
                      </span>
                    </div>
                    {emp.department && (
                      <div className={styles.empDetailItem}>
                        <span className={styles.empDetailLabel}>Dept:</span>
                        <span className={styles.empDetailVal}>{emp.department}</span>
                      </div>
                    )}
                    {emp.hire_date && (
                      <div className={styles.empDetailItem}>
                        <span className={styles.empDetailLabel}>Hired:</span>
                        <span className={styles.empDetailVal}>{emp.hire_date}</span>
                      </div>
                    )}
                  </div>

                  <div className={styles.empActions}>
                    <button className={styles.editBtnMobile} onClick={() => openEdit(emp)}>
                      Edit
                    </button>
                    <button className={styles.toggleBtnMobile} onClick={() => toggleStatus(emp)}>
                      {emp.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </div>
              ))}
              {employees.length === 0 && (
                <div className={styles.empty}>No employees yet.</div>
              )}
            </div>
          </>
        )}
      </div>

      {modal && (
        <Modal title={editing ? "Edit Employee" : "Add Employee"} onClose={() => setModal(false)}>
          <div className={styles.modalForm}>
            <div className={styles.grid2}>
              <FormField label="Full Name" required>
                <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Wanjiru" />
              </FormField>
              <FormField label="Email" required>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@royalhorizon.com" disabled={!!editing} />
              </FormField>
              <FormField label="Phone">
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="0712 345 678" />
              </FormField>
              <FormField label="Role">
                <Select value={form.role} onChange={(e) => set("role", e.target.value)}>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </Select>
              </FormField>
              <FormField label="Department">
                <Input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="e.g. Sales" />
              </FormField>
              <FormField label="Hire Date">
                <Input type="date" value={form.hire_date} onChange={(e) => set("hire_date", e.target.value)} />
              </FormField>
              {editing && (
                <FormField label="Status">
                  <Select value={form.status} onChange={(e) => set("status", e.target.value)}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                </FormField>
              )}
            </div>

            {!editing && (
              <p className={styles.inviteNote}>
                ℹ️ An invite email will be sent to the employee so they can set their password.
              </p>
            )}

            {err && <div className={styles.err}>{err}</div>}

            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setModal(false)}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : editing ? "Save Changes" : "Add Employee"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
